/**
 * Notification Preferences page — allows the authenticated user to
 * enable or disable individual notification types.
 *
 * Route: /settings/notifications
 *
 * Requirements: 12.4
 */

import { useState, useEffect, useCallback } from 'react';
import {
  notificationPreferencesService,
  type NotificationPreference,
} from '@/services/notificationPreferencesService';

// ─── Notification Type Display Metadata ────────────────────────────────

interface NotificationTypeInfo {
  type: string;
  name: string;
  description: string;
}

const NOTIFICATION_TYPE_INFO: NotificationTypeInfo[] = [
  { type: 'ContractExpiring', name: 'Contract Expiration Alerts', description: 'Receive alerts when contracts are approaching expiration (90/60/30 days)' },
  { type: 'ComplianceExpiring', name: 'Compliance Expiration Alerts', description: 'Receive alerts when compliance documents are expiring' },
  { type: 'RiskAlertDetected', name: 'Risk Alert Notifications', description: 'Receive notifications when new vendor risk alerts are detected' },
  { type: 'InvoicePendingReview', name: 'Invoice Review Queue', description: 'Receive notifications when new invoices need coding review' },
  { type: 'InvoicePendingApproval', name: 'Invoice Approval Queue', description: 'Receive notifications when invoices are submitted for VP approval' },
  { type: 'InvoiceApproved', name: 'Invoice Approved', description: 'Receive notifications when invoices are approved' },
  { type: 'InvoiceRejected', name: 'Invoice Rejected', description: 'Receive notifications when invoices are rejected' },
  { type: 'FlagForReview', name: 'Flag for Review', description: 'Receive notifications when a record is flagged for your review' },
  { type: 'OnboardingOverdue', name: 'Onboarding Overdue', description: 'Receive alerts when vendor onboarding items are overdue' },
  { type: 'SyncFailure', name: 'Sync Failure Alerts', description: 'Receive alerts when automated sync processes fail' },
  { type: 'ProcessFailure', name: 'Process Failure Alerts', description: 'Receive alerts when automated processes fail' },
  { type: 'BudgetThreshold', name: 'Budget Threshold Alerts', description: 'Receive alerts when budget utilization reaches warning thresholds' },
  { type: 'CircuitInstallComplete', name: 'Circuit Install Complete', description: 'Receive notifications when circuit installations are completed' },
  { type: 'CircuitDisconnectComplete', name: 'Circuit Disconnect Complete', description: 'Receive notifications when circuit disconnects are completed' },
  { type: 'SpendAnomaly', name: 'Spend Anomaly Alerts', description: 'Receive alerts when spend anomalies are detected' },
];

// ─── Component ─────────────────────────────────────────────────────────

export function NotificationPreferences() {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationPreferencesService.getPreferences();
      setPreferences(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notification preferences.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleToggle = (notificationType: string) => {
    setPreferences((prev) =>
      prev.map((p) =>
        p.notificationType === notificationType
          ? { ...p, enabled: !p.enabled }
          : p,
      ),
    );
    setDirty(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const updated = await notificationPreferencesService.updatePreferences({
        preferences,
      });
      setPreferences(updated);
      setDirty(false);
      setSuccess('Notification preferences saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notification preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div >
        <h1 className="text-lg font-semibold text-text-primary mb-6">Notification Preferences</h1>
        <p className="text-slate-400">Loading preferences…</p>
      </div>
    );
  }

  const prefMap = new Map(preferences.map((p) => [p.notificationType, p.enabled]));

  return (
    <div className=" max-w-3xl">
      <h1 className="text-lg font-semibold text-text-primary mb-2">Notification Preferences</h1>
      <p className="text-sm text-slate-500 mb-6">
        Choose which email notifications you would like to receive.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-6 py-4 text-green-700 text-sm mb-5" role="status">
          {success}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {NOTIFICATION_TYPE_INFO.map((info) => {
          const enabled = prefMap.get(info.type) ?? true;
          return (
            <label
              key={info.type}
              className={`flex items-start gap-3 px-4 py-3 border rounded-xl cursor-pointer transition-colors ${
                enabled
                  ? 'bg-white border-border'
                  : 'bg-page border-border'
              }`}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={() => handleToggle(info.type)}
                aria-label={`Toggle ${info.name}`}
                className="mt-0.5 w-4.5 h-4.5 cursor-pointer accent-baker-red"
              />
              <div>
                <div className="font-semibold text-text-primary text-sm">{info.name}</div>
                <div className="text-sm text-slate-500">{info.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="mt-6">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className={`px-6 py-2.5 rounded-lg text-white text-sm font-semibold ${
            dirty
              ? 'bg-baker-red hover:bg-baker-red/90 cursor-pointer'
              : 'bg-slate-400 cursor-not-allowed'
          } disabled:opacity-70`}
        >
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
